import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface Favorite {
  id: string;
  user_id: string;
  search_query: string;
  search_type: "oem" | "ref";
  platform: string;
  product_title: string;
  product_url: string | null;
  price: number | null;
  brand: string | null;
  reference: string | null;
  created_at: string;
  last_price_check: string | null;
}

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = async () => {
    if (!user) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_favorites")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFavorites((data || []) as Favorite[]);
    } catch (error: any) {
      console.error("Error fetching favorites:", error);
      toast.error("Erreur lors du chargement des favoris");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [user]);

  const addFavorite = async (
    searchQuery: string,
    searchType: "oem" | "ref",
    platform: string,
    productTitle: string,
    productUrl: string | null,
    price: number | null,
    brand: string | null,
    reference: string | null
  ) => {
    if (!user) {
      toast.error("Vous devez être connecté pour ajouter aux favoris");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("user_favorites")
        .insert({
          user_id: user.id,
          search_query: searchQuery,
          search_type: searchType,
          platform,
          product_title: productTitle,
          product_url: productUrl,
          price,
          brand,
          reference,
        })
        .select()
        .single();

      if (error) throw error;

      setFavorites((prev) => [data as Favorite, ...prev]);
      toast.success("Ajouté aux favoris");
      return data;
    } catch (error: any) {
      console.error("Error adding favorite:", error);
      toast.error("Erreur lors de l'ajout aux favoris");
    }
  };

  const removeFavorite = async (id: string) => {
    try {
      const { error } = await supabase
        .from("user_favorites")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setFavorites((prev) => prev.filter((f) => f.id !== id));
      toast.success("Retiré des favoris");
    } catch (error: any) {
      console.error("Error removing favorite:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const isFavorite = (platform: string, productTitle: string, reference: string | null) => {
    // Use reference for unique identification if available, otherwise fall back to title
    if (reference) {
      return favorites.some(
        (f) => f.platform === platform && f.reference === reference
      );
    }
    return favorites.some(
      (f) => f.platform === platform && f.product_title === productTitle
    );
  };

  const getFavoriteId = (platform: string, productTitle: string, reference: string | null): string | null => {
    let favorite;
    if (reference) {
      favorite = favorites.find(
        (f) => f.platform === platform && f.reference === reference
      );
    } else {
      favorite = favorites.find(
        (f) => f.platform === platform && f.product_title === productTitle
      );
    }
    return favorite?.id || null;
  };

  return {
    favorites,
    loading,
    addFavorite,
    removeFavorite,
    isFavorite,
    getFavoriteId,
    refetch: fetchFavorites,
  };
};
